import { trpc } from "@/trpc/client";

const slugCache = new Map();
const slugRequestCache = new Map();

const extractCampaignIdFromString = (value) => {
  if (!value || typeof value !== "string") return null;

  const normalized = value.replace(/\\"/g, '"');
  const patterns = [
    /campaigns\.campaign_id["']?\s*[:=]\s*["']?(\d+)/i,
    /campaign_id["']?\s*[:=]\s*["']?(\d+)/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[1]) {
      const parsed = Number(match[1]);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }

  return null;
};

const inferCampaignId = (variables = {}) => {
  const directCandidates = [
    variables.campaignId,
    variables.campaign_id,
    variables.totalDataCampaignId2,
  ];

  for (const candidate of directCandidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  const stringCandidates = [
    variables.filter,
    variables.campaignFilter2,
    variables.filterSite,
  ];

  for (const candidate of stringCandidates) {
    const parsed = extractCampaignIdFromString(candidate);
    if (parsed) return parsed;
  }

  return null;
};

const getSiteIdFromObject = (value) => {
  if (!value || typeof value !== "object") return null;

  const candidates = [
    value.site_id,
    value.site?.site_id,
    value._id?.site_id,
    value._id?.site?.site_id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return null;
};

const applySlugRecursively = (value, slugMap) => {
  if (Array.isArray(value)) {
    return value.map((item) => applySlugRecursively(item, slugMap));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const cloned = Object.keys(value).reduce((acc, key) => {
    acc[key] = applySlugRecursively(value[key], slugMap);
    return acc;
  }, {});

  if (typeof cloned.site_name === "string") {
    const siteId = getSiteIdFromObject(cloned);
    const slug = siteId ? slugMap.get(siteId) : null;
    if (slug) {
      cloned.site_name = slug;
    }
  }

  return cloned;
};

export const clearCampaignSiteSlugCache = (campaignId) => {
  if (campaignId) {
    const cacheKey = String(campaignId);
    slugCache.delete(cacheKey);
    slugRequestCache.delete(cacheKey);
    return;
  }
  slugCache.clear();
  slugRequestCache.clear();
};

export const applyCampaignSiteSlugNames = async (payload, variables = {}) => {
  const campaignId = inferCampaignId(variables);
  if (!campaignId) return payload;

  const cacheKey = String(campaignId);
  let slugMap = slugCache.get(cacheKey);

  if (!slugMap) {
    if (!slugRequestCache.has(cacheKey)) {
      slugRequestCache.set(
        cacheKey,
        trpc.lookups.campaigns.siteSlugs
          .query({ campaign_id: campaignId })
          .then((result) => {
            const mappedSlugMap = new Map(
              (result?.rows || [])
                .map((item) => ({
                  siteId: Number(item?.site_id),
                  slug: String(item?.site_slug_name || "").trim(),
                }))
                .filter((item) => Number.isFinite(item.siteId) && item.siteId > 0 && item.slug)
                .map((item) => [item.siteId, item.slug])
            );

            slugCache.set(cacheKey, mappedSlugMap);
            return mappedSlugMap;
          })
          .finally(() => {
            slugRequestCache.delete(cacheKey);
          })
      );
    }

    try {
      slugMap = await slugRequestCache.get(cacheKey);
    } catch {
      return payload;
    }
  }

  if (!slugMap?.size) return payload;
  return applySlugRecursively(payload, slugMap);
};
